import { Injectable } from '@angular/core';
import { CsvParseResult, EmployerCorrectionCsvRow } from '../models/employer-correction.model';

// Accepted header aliases per column, matching the on-screen table.
const SERIAL_HEADERS = ['serial', 'branchserial', 'branch serial'];
const TNUMBER_HEADERS = ['tnumber', 'tnr', 'employertnr'];
const NAME_HEADERS = ['name', 'incorrectspelling', 'currentname', 'current name (incorrect)'];
const CORRECTED_HEADERS = ['correctedspelling', 'correctedname', 'corrected name'];

@Injectable({ providedIn: 'root' })
export class CsvParserService {

  /**
   * Parses CSV text into employer name-correction rows.
   * - Requires header row with serial, tnumber, name and corrected-name columns (aliases supported).
   * - Skips rows missing any required field, or with a non-numeric TNumber.
   * - Normalises \r\n and \r line endings to \n before splitting.
   */
  parse(csvText: string): CsvParseResult {
    const normalised = csvText.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const lines = normalised.split('\n');

    if (lines.length === 0 || lines[0].trim() === '') {
      return { rows: [], skippedCount: 0, skippedWarnings: ['CSV file is empty or has no header row'] };
    }

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());

    const serialIndex = headers.findIndex(h => SERIAL_HEADERS.includes(h));
    const tnumberIndex = headers.findIndex(h => TNUMBER_HEADERS.includes(h));
    const nameIndex = headers.findIndex(h => NAME_HEADERS.includes(h));
    const correctedIndex = headers.findIndex(h => CORRECTED_HEADERS.includes(h));

    const missingColumns: string[] = [];
    if (serialIndex === -1) missingColumns.push('serial');
    if (tnumberIndex === -1) missingColumns.push('tnumber');
    if (nameIndex === -1) missingColumns.push('name');
    if (correctedIndex === -1) missingColumns.push('correctedSpelling');

    if (missingColumns.length > 0) {
      return {
        rows: [],
        skippedCount: 0,
        skippedWarnings: [`CSV is missing required columns: ${missingColumns.join(', ')}`],
      };
    }

    const rows: EmployerCorrectionCsvRow[] = [];
    const skippedWarnings: string[] = [];
    let skippedCount = 0;

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      if (line.trim() === '') {
        continue;
      }

      const cells = line.split(',');
      const rowNumber = i + 1; // 1-based, row 1 is the header

      const serial = (cells[serialIndex] ?? '').trim();
      const tnumberRaw = (cells[tnumberIndex] ?? '').trim();
      const name = (cells[nameIndex] ?? '').trim();
      const correctedSpelling = (cells[correctedIndex] ?? '').trim();

      if (serial === '') {
        skippedWarnings.push(`Row ${rowNumber} skipped: empty serial`);
        skippedCount++;
        continue;
      }

      const tnumber = Number.parseInt(tnumberRaw, 10);
      if (tnumberRaw === '' || Number.isNaN(tnumber)) {
        skippedWarnings.push(`Row ${rowNumber} skipped: TNumber is not a valid number`);
        skippedCount++;
        continue;
      }

      if (name === '') {
        skippedWarnings.push(`Row ${rowNumber} skipped: empty name`);
        skippedCount++;
        continue;
      }

      if (correctedSpelling === '') {
        skippedWarnings.push(`Row ${rowNumber} skipped: corrected name is empty`);
        skippedCount++;
        continue;
      }

      rows.push({ serial, tnumber, name, correctedSpelling });
    }

    return { rows, skippedCount, skippedWarnings };
  }

  /**
   * Reads a File using the browser FileReader API and resolves with
   * the parsed CsvParseResult, or rejects if the file cannot be read.
   */
  parseFile(file: File): Promise<CsvParseResult> {
    return new Promise<CsvParseResult>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event: ProgressEvent<FileReader>) => {
        const text = event.target?.result as string;
        resolve(this.parse(text));
      };

      reader.onerror = () => {
        const errorMessage = reader.error?.message ?? 'Unknown error';
        reject(new Error(`Failed to read file: ${errorMessage}`));
      };

      reader.readAsText(file);
    });
  }
}
